<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

export interface BreadcrumbItem {
  label: string
  onClick?: () => void
}

interface Props {
  crumbs: BreadcrumbItem[]
  onBack: () => void
}

defineProps<Props>()
</script>

<template>
  <Box flex align="center" gap="3" class="page-nav">
    <Box
      is="button"
      flex
      align="center"
      justify="center"
      class="page-nav__back-btn"
      type="button"
      aria-label="Go back"
      @click="onBack"
    >
      <PiIcon name="chevron-left" class="page-nav__back-icon" />
    </Box>
    <Box flex align="center" gap="2" class="page-nav__breadcrumb">
      <template v-for="(crumb, index) in crumbs" :key="index">
        <PiIcon v-if="index > 0" name="chevron-right" class="page-nav__sep" />
        <Text
          type="body"
          :weight="index === crumbs.length - 1 ? 'semibold' : 'medium'"
          :color="index === crumbs.length - 1 ? 'default' : 'muted'"
          class="page-nav__crumb"
          :class="{
            'page-nav__crumb--link': !!crumb.onClick,
            'page-nav__crumb--active': index === crumbs.length - 1,
          }"
          @click="crumb.onClick?.()"
        >
          {{ crumb.label }}
        </Text>
      </template>
    </Box>
  </Box>
</template>

<style scoped>
.page-nav {
  flex-shrink: 0;
}

/* Bespoke 22x22 icon-only back button — not a Button preset, so kept as a scoped class. */
.page-nav__back-btn {
  width: 22px;
  height: 22px;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}

.page-nav__back-btn:hover {
  background: var(--p-surface-100);
}

.page-nav__back-icon {
  font-size: 12px;
  color: var(--p-text-color);
}

.page-nav__breadcrumb {
  min-width: 0;
}

.page-nav__sep {
  font-size: 10px;
  color: var(--p-text-muted-color);
  flex-shrink: 0;
}

.page-nav__crumb {
  white-space: nowrap;
}

.page-nav__crumb--link {
  cursor: pointer;
  flex-shrink: 0;
}

.page-nav__crumb--link:hover {
  color: var(--p-primary-500);
}

.page-nav__crumb--active {
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 992px) {
  .page-nav {
    gap: 12px;
  }
}
</style>
